<template>
  <!-- Bottom safe-area spacer (so page content isn't hidden behind the bar) -->
  <div class="md:hidden" style="height: calc(4rem + env(safe-area-inset-bottom))" />

  <nav
    class="fixed bottom-0 left-0 right-0 z-50 md:hidden flex items-stretch"
    style="background:rgba(26, 51, 38, 0.7); backdrop-filter: blur(16px) saturate(180%); -webkit-backdrop-filter: blur(16px) saturate(180%); border-top:1px solid rgba(255,255,255,0.15); box-shadow: 0 -4px 24px rgba(0,0,0,0.2); height: calc(4rem + env(safe-area-inset-bottom)); padding-bottom:env(safe-area-inset-bottom);"
    :aria-label="$t('ui.mainNavigation')"
  >
    <NavBtn
      v-for="item in primaryItems"
      :key="item.key"
      :to="item.to"
      :active="item.activeMatch(route.path)"
      :label="$t(item.labelKey)"
    >
      <div class="relative">
        <component :is="item.icon" class="w-5 h-5" />
        <span
          v-if="item.key === 'checkout' && store.checkoutItemList.length > 0"
          class="absolute -top-1.5 -right-2.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full text-[8px] font-bold leading-none bg-emerald-500 text-white transition-transform duration-300"
          :class="{ 'scale-125': store.checkoutBadgePulse }"
        >{{ store.checkoutItemList.length }}</span>
      </div>
    </NavBtn>

    <!-- More: toggles slide-up panel -->
    <button
      ref="moreButton"
      @click="moreOpen = !moreOpen"
      class="flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors cursor-pointer"
      :class="(moreOpen || moreIsActive) ? 'text-green-300!' : 'text-eq-muted'"
      :aria-label="$t('common.more')"
      :aria-expanded="moreOpen"
      aria-controls="mobile-more-navigation"
    >
      <div class="relative">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5">
          <circle cx="12" cy="5"  r="1" fill="currentColor"/><circle cx="12" cy="12" r="1" fill="currentColor"/><circle cx="12" cy="19" r="1" fill="currentColor"/>
        </svg>
        <span v-if="moreIsActive && !moreOpen" class="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-green-400" />
      </div>
      <span class="text-[10px] leading-none cursor-pointer">{{ $t('common.more', $t('ui.more')) }}</span>
    </button>
  </nav>

  <!-- "More" slide-up panel -->
  <Transition name="slide-up">
    <div
      v-if="moreOpen"
      id="mobile-more-navigation"
      @keydown.esc="closeMore"
      class="fixed left-0 right-0 z-50 md:hidden rounded-t-2xl overflow-hidden shadow-2xl"
      style="bottom: calc(4rem + env(safe-area-inset-bottom)); background:rgba(22, 40, 30, 0.92); backdrop-filter: blur(20px) saturate(180%); -webkit-backdrop-filter: blur(20px) saturate(180%); border-top:1px solid rgba(255,255,255,0.12); max-height: 75vh; overflow-y: auto;"
    >
      <!-- Drag handle -->
      <div class="pt-3 pb-1 flex justify-center sticky top-0" style="background:inherit;">
        <div class="w-10 h-1 rounded-full bg-white/20" />
      </div>

      <!-- Sliding viewport — two panels side by side, controlled by transform -->
      <div class="relative overflow-hidden">
        <div
          class="flex transition-transform duration-250 ease-in-out"
          :style="{ transform: activeGroup ? 'translateX(-50%)' : 'translateX(0)', width: '200%' }"
        >
          <!-- ── Level 1: Category groups ─────────────────────────────────── -->
          <div class="w-1/2 p-4 pt-2" :inert="!!activeGroup">
            <p class="text-xs uppercase text-gray-500 font-semibold tracking-wider mb-3 px-1">{{ $t('nav.groups.browse') }}</p>
            <div class="flex flex-col gap-2">
              <button
                v-for="group in availableGroups"
                :key="group.key"
                @click="openGroup(group.key)"
                class="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-left w-full"
                style="background: rgba(255,255,255,0.05);"
              >
                <component :is="group.icon" class="w-5 h-5 shrink-0" :class="group.iconClass" />
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-white leading-tight">{{ $t(group.labelKey) }}</div>
                  <div class="text-xs text-gray-400 mt-0.5">{{ $t(group.descriptionKey) }}</div>
                </div>
                <MdiChevronRight class="w-4 h-4 text-gray-500 shrink-0" />
              </button>
            </div>

            <button
              @click="logout"
              class="mt-4 w-full btn danger text-sm py-3 rounded-xl"
            >{{ $t('common.logOut') }}</button>
          </div>

          <!-- ── Level 2: Items in selected group ───────────────────────────── -->
          <div class="w-1/2 p-4 pt-2" :inert="!activeGroup">
            <button
              @click="activeGroup = null"
              class="flex items-center gap-1 text-sm text-green-400 mb-3 hover:text-green-300 transition-colors"
            >
              <MdiChevronLeft class="w-4 h-4" />
              <span>{{ activeGroupMeta ? $t(activeGroupMeta.labelKey) : '' }}</span>
            </button>
            <div class="grid grid-cols-3 gap-3">
              <MoreBtn
                v-for="item in activeGroupItems"
                :key="item.to"
                :to="item.to"
                :label="$t(item.labelKey)"
                :icon="item.icon"
                :icon-class="item.iconClass"
                @click="closeMore"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="h-2" />
    </div>
  </Transition>
  <div v-if="moreOpen" class="fixed inset-0 z-40 md:hidden" @click="moreOpen = false" />
</template>

<script setup>
import { ref, computed, watch, defineComponent, h } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { logOutApi } from '@/api/api.js';
import { useStore } from '@/stores/store.js';
import { usePermissions } from '@/composables/usePermissions.js';
import { bottomPrimaryItems, bottomMoreItems } from '@/config/navItems.js';
import MdiChevronRight from '~icons/mdi/chevron-right';
import MdiChevronLeft from '~icons/mdi/chevron-left';
import MdiClipboardList from '~icons/mdi/clipboard-list';
import MdiDomain from '~icons/mdi/domain';
import MdiShieldAccount from '~icons/mdi/shield-account';

import { useShellModeStore } from '@/stores/shellMode.js';

const route  = useRoute();
const store  = useStore();
const perms  = usePermissions();
const shell  = useShellModeStore();
const moreOpen   = ref(false);
const moreButton = ref(null);
const activeGroup = ref(null); // null | 'operations' | 'management' | 'administration'

/** Returns true if the item is visible to the current user */
function itemVisible(item) {
  if (item.shellModes?.length && !item.shellModes.includes(shell.mode)) return false;
  if (!item.requiredPermission) return true;
  return perms[item.requiredPermission]?.value ?? false;
}

const primaryItems = computed(() => bottomPrimaryItems.filter(itemVisible));

const moreIsActive = computed(() =>
  bottomMoreItems.some(item => {
    if (!itemVisible(item)) return false;
    return item.activeMatch(route.path);
  })
);

// Group metadata for the Level 1 panel
const GROUP_META = {
  operations: {
    labelKey: 'nav.groups.operations',
    descriptionKey: 'nav.groups.operationsDesc',
    icon: MdiClipboardList,
    iconClass: 'text-emerald-400',
  },
  management: {
    labelKey: 'nav.groups.management',
    descriptionKey: 'nav.groups.managementDesc',
    icon: MdiDomain,
    iconClass: 'text-sky-400',
  },
  administration: {
    labelKey: 'nav.groups.administration',
    descriptionKey: 'nav.groups.administrationDesc',
    icon: MdiShieldAccount,
    iconClass: 'text-red-400',
  },
};

// All 'more' items filtered by role, grouped by category
const groupedItems = computed(() => {
  const groups = {};
  for (const item of bottomMoreItems) {
    if (!item.category) continue;
    if (!itemVisible(item)) continue;
    if (!groups[item.category]) groups[item.category] = [];
    groups[item.category].push(item);
  }
  return groups;
});

// Groups available to the current user (only show groups that have visible items)
const availableGroups = computed(() =>
  Object.entries(GROUP_META)
    .filter(([key]) => (groupedItems.value[key] ?? []).length > 0)
    .map(([key, meta]) => ({ key, ...meta }))
);

const activeGroupMeta = computed(() =>
  activeGroup.value ? { ...GROUP_META[activeGroup.value], key: activeGroup.value } : null
);

const activeGroupItems = computed(() =>
  activeGroup.value ? (groupedItems.value[activeGroup.value] ?? []) : []
);

function openGroup(key) {
  activeGroup.value = key;
}

function closeMore() {
  moreOpen.value = false;
  moreButton.value?.focus();
}

// Reset to Level 1 whenever the panel is closed
watch(moreOpen, (open) => {
  if (!open) activeGroup.value = null;
});

// Inline NavBtn component
const NavBtn = defineComponent({
  props: { to: String, active: Boolean, label: String },
  setup(props, { slots }) {
    return () => h(
      RouterLink,
      {
        to: props.to,
        class: [
          'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors',
          props.active ? 'text-green-300' : 'text-gray-400',
        ],
      },
      [
        slots.default?.(),
        h('span', { class: 'text-[10px] leading-none' }, props.label),
      ]
    );
  },
});

const MoreBtn = defineComponent({
  props: { to: String, label: String, icon: Object, iconClass: String },
  emits: ['click'],
  setup(props, { emit }) {
    return () => h(
      RouterLink,
      {
        to: props.to,
        onClick: () => emit('click'),
        class: 'flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-white/5 active:bg-white/10 transition-colors text-gray-300',
      },
      [
        h(props.icon, { class: ['w-6 h-6', props.iconClass].filter(Boolean).join(' ') }),
        h('span', { class: 'text-xs text-center leading-tight' }, props.label),
      ]
    );
  },
});

const logout = async () => {
  await logOutApi();
};
</script>

<style scoped>
.slide-up-enter-active, .slide-up-leave-active { transition: transform .2s ease, opacity .2s ease; }
.slide-up-enter-from, .slide-up-leave-to       { transform: translateY(100%); opacity: 0; }
</style>

